import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RightOutlined } from '@ant-design/icons';
import { Divider, Image, message, Modal, Skeleton, Table } from 'antd';
import api from '@/services/api';
import useAddTab from '@/hooks/assistant/useAddTab';

export const ResultVinnNumberModal = ({ vinn, cb }) => {
  const [loading, setLoading] = React.useState(false);
  const { t } = useTranslation();
  const [data, setData] = React.useState([]);
  const addTab = useAddTab();

  useEffect(() => {
    if (vinn) {
      setLoading(true);
      api()
        .post('/catalog/search-vinn-number', {
          Vinn: vinn,
        })
        .then((e) => {
          setData(e.data.data.sort((a, b) => a?.LineName?.localeCompare(b?.LineName)));
          if (e.data.data.length === 1) {
            let item = e.data.data[0];
            addTab(
              {
                vehicleLineId: item.LineCode,
                vehicleLineLineName: item.LineName,
                section: 'vehicleMenu',
                make: item.MakeCode,
                plate: null,
                makeName: item.VehicleMakeName,
                type: 'assistant',
                vehicleSeriesName: item.VehicleReferenceName,
                vehicleSeriesId: item.VehicleReferenceId,
                title: item.VehicleMakeName + ' ' + item.LineName + ' ' + item.VehicleReferenceName,
              },
              'redirect'
            );
            cb();
          }
        })
        .catch((f) => {
          message.error(t('vinn-not-found'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [vinn]);

  const SelectArea = ({ children, item }) => {
    const onRowClick = () => {
      api()
        .post('/catalog/validate-type-code', {
          Vinn: vinn,
          SeriesId: item.VehicleReferenceId,
        });
      addTab(
        {
          vehicleLineId: item.LineCode,
          vehicleLineLineName: item.LineName,
          section: 'vehicleMenu',
          make: item.MakeCode,
          plate: null,
          makeName: item.VehicleMakeName,
          type: 'assistant',
          vehicleSeriesName: item.VehicleReferenceName,
          vehicleSeriesId: item.VehicleReferenceId,
          title: item.VehicleMakeName + ' ' + item.LineName + ' ' + item.VehicleReferenceName,
        },
        'redirect'
      );
      cb();
    };

    return (
      <div
        onClick={() => {
          onRowClick(item);
        }}
        style={{
          cursor: 'pointer',
        }}
      >
        {children}
      </div>
    );
  };

  const generateFilters = (key) => {
    const uniqueValues = [...new Set(data.map((item) => item[key]))];
    return uniqueValues.map((value) => ({ text: value, value }));
  };

  const columns = [
    {
      title: 'Make',
      dataIndex: 'VehicleMakeName',
      key: 'VehicleMakeName',
      filters: generateFilters('VehicleMakeName'),
      onFilter: (value, record) => record.VehicleMakeName === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Line Name',
      dataIndex: 'LineName',
      key: 'LineName',
      filters: generateFilters('LineName'),
      onFilter: (value, record) => record.LineName === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Engine Type',
      dataIndex: 'EngineType',
      key: 'EngineType',
      filters: generateFilters('EngineType'),
      onFilter: (value, record) => record.EngineType === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Vehicle Reference',
      dataIndex: 'VehicleReferenceName',
      key: 'VehicleReferenceName',
      filters: generateFilters('VehicleReferenceName'),
      onFilter: (value, record) => record.VehicleReferenceName === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Drive Type',
      dataIndex: 'DriveType',
      key: 'DriveType',
      filters: generateFilters('DriveType'),
      onFilter: (value, record) => record.DriveType === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Body Type',
      dataIndex: 'BodyType',
      key: 'BodyType',
      filters: generateFilters('BodyType'),
      onFilter: (value, record) => record.BodyType === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Engine Contents (cc)',
      dataIndex: 'EngineContents',
      key: 'EngineContents',
      filters: generateFilters('EngineContents'),
      onFilter: (value, record) => record.EngineContents === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'Fuel Type',
      dataIndex: 'FuelCode',
      key: 'FuelCode',
      filters: generateFilters('FuelCode'),
      onFilter: (value, record) => record.FuelCode === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: 'KW',
      dataIndex: 'KWCat',
      key: 'KWCat',
      filters: generateFilters('KWCat'),
      onFilter: (value, record) => record.KWCat === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: t('horse-power'),
      dataIndex: 'HorsePowerFrom',
      key: 'HorsePower',
      filters: generateFilters('HorsePowerFrom'),
      onFilter: (value, record) => record.HorsePowerFrom === value,
      render: (text, record) => {
        record.HorsePowerFrom = record.HorsePowerFrom ? record.HorsePowerFrom : 0;
        record.HorsePowerTo = record.HorsePowerTo ? record.HorsePowerTo : 0;

        if(!(record.HorsePowerFrom || record.HorsePowerTo)) {
            return null
        }

        return <SelectArea item={record}>{`${record.HorsePowerFrom} - ${record.HorsePowerTo}`}</SelectArea>;
      },
    },
    {
      title: t('cylinder'),
      dataIndex: 'TotalCylinders',
      key: 'TotalCylinders',
      filters: generateFilters('TotalCylinders'),
      onFilter: (value, record) => record.TotalCylinders === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: t('build-start'),
      dataIndex: 'BuildStart',
      key: 'BuildPeriod',
      filters: generateFilters('BuildStart'),
      onFilter: (value, record) => record.BuildStart === value,
      render: (text, record) => {
        return <SelectArea item={record}>{`${record.BuildStart} - ${record.BuildEnd}`}</SelectArea>;
      },
    },
    {
      title: t('engine'),
      dataIndex: 'Engines',
      key: 'Engines',
      filters: generateFilters('Engines'),
      onFilter: (value, record) => record.Engines === value,
      render: (text, record) => {
        return <SelectArea item={record}>{text}</SelectArea>;
      },
    },
    {
      title: t('photo'),
      dataIndex: 'Photo',
      key: 'Photo',
      render: (text, record) => {
        return (
          <SelectArea item={record}>
            <Image src={record.Photo} height={40} />
          </SelectArea>
        );
      },
    },
  ];


  return (
    <div>
      <Skeleton loading={loading} active={true}>
        <Table dataSource={data} columns={columns} rowKey="VehicleReferenceId" />
      </Skeleton>
    </div>
  );
};

const ResultVinnNumber = ({ reducer, text = 'VINN' }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = React.useState(false);

  return (
    <>
      <Modal
        width={'100%'}
        title={'VINN Number'}
        footer={null}
        zIndex={1050}
        open={showModal}
        onCancel={() => setShowModal(false)}
      >
        <ResultVinnNumberModal cb={() => setShowModal(false)} key={reducer.state.text} vinn={reducer.state.vinnNumber} />
      </Modal>

      <div className="flex">
        <p style={{ color: '#A5A5A5', fontSize: '20px' }}>{t(text)}</p>
        <div className="flex items-center cursor-pointer" onClick={() => setShowModal(true)}>
          {<RightOutlined />}
          {
            <p style={{ fontWeight: 500, fontSize: '13px', padding: 0 }} className="ml-2">
              <a style={{ color: '#2b94c8' }}>{reducer.state.vinnNumber?.toUpperCase()}</a>
            </p>
          }
        </div>
      </div>
      <Divider />
    </>
  );
};

export default ResultVinnNumber;
